import { ref } from 'vue'
import type { StateMapStats, StateSalary } from './useStateSalaryData'
import { formatCurrency } from '~/utils/formatters'
import { useGlobalSalaryPeriod } from '~/composables/useGlobalSalaryPeriod'
import { usStatePaths } from '~/utils/us-state-paths'

export default function useMapVisualization(mapStats: Ref<StateMapStats>) {
  const { adjustValueForPeriod } = useGlobalSalaryPeriod()
  const hoveredState = ref<StateSalary | null>(null)
  const tooltipPosition = ref({ x: 0, y: 0 })
  const getStateColor = (salary: number) => {
    const colors = {
      100: '#e0e7ff',
      200: '#c7d2fe',
      300: '#a5b4fc',
      400: '#818cf8',
      500: '#6366f1',
      600: '#4f46e5'
    }
    if (!salary) return '#f5f5f5'
    const min = mapStats.value.min_salary
    const max = mapStats.value.max_salary
    if (max === min) {
      const intensityLevels = [100, 200, 300, 400, 500, 600]
      const level
        = intensityLevels[Math.floor(Math.random() * intensityLevels.length)]
      return colors[level as keyof typeof colors]
    }
    const ratio = (salary - min) / (max - min)
    const level = Math.round(ratio * 5) * 100 + 100
    const clampedLevel = Math.min(600, Math.max(100, level)) as
      | 100
      | 200
      | 300
      | 400
      | 500
      | 600
    return colors[clampedLevel]
  }
  const getStatePath = (stateCode: string) => {
    return usStatePaths[stateCode.toLowerCase()] || ''
  }
  const getStateBorder = (stateCode: string) => {
    return ['AK', 'HI'].includes(stateCode)
      ? '#666'
      : 'rgba(255, 255, 255, 0.5)'
  }
  const handleMouseOver = (
    event: MouseEvent | TouchEvent,
    state: StateSalary,
    position?: { x: number, y: number }
  ) => {
    if (['AK', 'HI'].includes(state.state.toUpperCase())) {
      hoveredState.value = null
      return
    }
    hoveredState.value = state
    if (position) {
      tooltipPosition.value = {
        x: position.x + 10,
        y: position.y + 10
      }
    } else {
      let clientX: number
      let clientY: number
      if ('touches' in event) {
        clientX = event.touches[0].clientX
        clientY = event.touches[0].clientY
      } else {
        clientX = event.clientX
        clientY = event.clientY
      }
      tooltipPosition.value = {
        x: clientX + 10,
        y: clientY + 10
      }
    }
  }
  const handleMouseOut = () => {
    hoveredState.value = null
  }
  const handleTouchEnd = () => {
    setTimeout(() => {
      hoveredState.value = null
    }, 1500)
  }
  const getFormattedSalary = (state: StateSalary): string => {
    return formatCurrency(adjustValueForPeriod(state.median_salary))
  }
  return {
    hoveredState,
    tooltipPosition,
    getStateColor,
    getStatePath,
    getStateBorder,
    handleMouseOver,
    handleMouseOut,
    handleTouchEnd,
    getFormattedSalary
  }
}
